import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSubjects } from '../../hooks/useSubjects';
import SubjectForm from '../../components/forms/SubjectForm';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const SubjectEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { subjects, updateSubject } = useSubjects();
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Find the subject by ID
    const foundSubject = subjects.find(s => s.id === id);
    if (foundSubject) {
      setSubject(foundSubject);
    } else if (subjects.length > 0) {
      // If subjects are loaded but not found
      setNotFound(true);
    }
  }, [id, subjects]);

  const handleSubmit = async (formData) => {
    setLoading(true);
    try {
      const result = await updateSubject(id, formData);
      if (result.success) {
        alert('Subject updated successfully!');
        navigate('/subjects');
      } else {
        alert(result.error || 'Failed to update subject');
      }
    } catch (error) {
      alert('An unexpected error occurred');
      console.error('Error updating subject:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/subjects');
  };

  if (notFound) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button 
            variant="outline" 
            onClick={handleCancel}
            className="flex items-center"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-gray-900">Subject Not Found</h1>
        </div>
        <Card>
          <div className="py-8 text-center">
            <p className="text-gray-500">The subject you are trying to edit does not exist.</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!subject) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button 
            variant="outline" 
            onClick={handleCancel}
            className="flex items-center"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-gray-900">Edit Subject</h1>
        </div>
        <Card>
          <div className="py-8 text-center">
            <p className="text-gray-500">Loading subject data...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button 
          variant="outline" 
          onClick={handleCancel}
          className="flex items-center"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          Back
        </Button>
        <h1 className="text-xl font-bold text-gray-900">Edit Subject</h1>
      </div>
      
      <Card>
        <SubjectForm
          initialData={subject}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={loading}
        />
      </Card>
    </div>
  );
};

export default SubjectEdit;